/* ═══════════════════════════════════════════════════════════════════════
   useHasilGenerateTertinggal — Workspace mengambil hasil Generate yang
   tertinggal (2026-10-26). Peraturan + bentuk doc: utils/hasilGenerateTertinggal.js

   Kenapa: dahulu hasil Generate hanya sampai ke page yang memanggil. Pelanggan
   ke page lain / refresh / Chrome tidurkan tab → server siap, kuota terpotong,
   kad kosong tanpa loading. Kini:
     • kad yang masih dijana (oleh page yang sudah ditutup) → loading muncul semula
     • hasil yang siap semasa page tiada → ditulis ke kad bila page dibuka semula
     • gagal / terhenti → kad GAGAL + butang Retry, bukan kosong senyap

   Pembahagian kerja dengan generateForProduct (WorkspaceView):
     • `sesiIni.milik` (Set progressId) — Generate yang dimulakan OLEH PAGE INI.
       Hook tidak menyentuh doc itu; laluan biasa mengambil hasil + memadam doc.
       Bila laluan biasa GAGAL (cth had masa 5 min client) ia membuang progressId
       dari `milik` → hook mengambil alih (hasil yang siap lewat masih masuk kad).
     • `sesiIni.aktif` (Set productId) — kad yang sedang dijana oleh page ini.
       Hook tidak menulis kad itu langsung (elak hasil lama menindih hasil baharu).
   ═══════════════════════════════════════════════════════════════════════ */

import { useEffect, useRef, useState } from 'react';
import { collection, doc, onSnapshot, runTransaction, deleteDoc, query, where, getDocs } from 'firebase/firestore';
import { db } from '../firebase';
import { kelasDocProgress, bakiTungguPemilik, bacaHasil, kunciStatus } from '../utils/hasilGenerateTertinggal';

const KOLEKSI = 'generation_progress';
const refDoc = (uid, id) => doc(db, 'users', uid, KOLEKSI, id);

/** Ambil + padam doc secara atomik. null = sudah diambil oleh page lain. */
async function tuntutDoc(uid, id) {
    return runTransaction(db, async (tx) => {
        const ref = refDoc(uid, id);
        const snap = await tx.get(ref);
        if (!snap.exists()) return null;
        tx.delete(ref);
        return snap.data();
    });
}

/** Laluan biasa memadam doc selepas hasil ditulis ke kad. */
export async function padamDocProgress(uid, id) {
    if (!uid || !id) return;
    try { await deleteDoc(refDoc(uid, id)); }
    catch (e) { console.warn('[HasilTertinggal] Padam doc progress gagal:', id, e?.message); }
}

/** Generate BARU bermula untuk kad ini → doc lama kad yang sama (gagal / tertinggal) sudah basi.
 *  Dibuang supaya hasil lama tidak menindih hasil baharu kemudian. */
export async function buangDocLainUntukKad(uid, productId, kecualiId) {
    if (!uid || !productId) return;
    try {
        const snap = await getDocs(query(collection(db, 'users', uid, KOLEKSI), where('productId', '==', productId)));
        await Promise.all(snap.docs.filter(d => d.id !== kecualiId).map(d => deleteDoc(d.ref)));
    } catch (e) {
        console.warn('[HasilTertinggal] Buang doc lama kad gagal:', productId, e?.message);
    }
}

/**
 * @param {object} p
 * @param {string|null} p.uid
 * @param {Array} p.products            state kad Workspace
 * @param {{current:{milik:Set,aktif:Map}}} p.sesiIni   aktif = productId -> bilangan larian (guna .has sahaja)
 * @param {object} p.pengendali         { onJalan(pid,data), sedia(pid):Promise, onSiap(pid,hasil):Promise, onGagal(pid,{jenis,mesej,kod}), onHilang(pid) }
 */
export function useHasilGenerateTertinggal({ uid, products, sesiIni, pengendali }) {
    const [docs, setDocs] = useState([]);
    const pengendaliRef = useRef(pengendali);
    // Pengendali terkini (elak closure basi). Effect ini diisytihar SEBELUM effect pemproses
    // di bawah → React menjalankannya dahulu dalam setiap commit.
    useEffect(() => { pengendaliRef.current = pengendali; });

    const dikendaliRef = useRef(new Set());     // docId sudah dituntut / sedang dituntut
    const dijadualRef = useRef(new Map());      // docId -> timeout (doc siap, tunggu pemilik)
    const dicerminkanRef = useRef(new Map());   // productId -> docId (loading yang hook papar)
    const statusRef = useRef(new Map());        // docId -> kunciStatus terakhir

    useEffect(() => {
        if (!uid) return undefined;
        const unsub = onSnapshot(
            collection(db, 'users', uid, KOLEKSI),
            (snap) => setDocs(snap.docs.map(d => ({ id: d.id, data: d.data() }))),
            (err) => console.warn('[HasilTertinggal] Pendengar peti progress gagal — hasil Generate dari page yang ditutup tidak akan diambil:', err?.message)
        );
        const dijadual = dijadualRef.current;
        return () => {
            unsub();
            dijadual.forEach(t => clearTimeout(t));
            dijadual.clear();
        };
    }, [uid]);

    useEffect(() => {
        if (!uid) return;
        const sekarang = Date.now();
        const milik = sesiIni.current.milik;
        const aktif = sesiIni.current.aktif;
        const kadIkutId = new Map((products || []).map(p => [p.id, p]));
        const kadSudahDimuat = kadIkutId.size > 0;
        const idAda = new Set(docs.map(d => d.id));

        const tuntutDanGagal = (d, gagal) => {
            dikendaliRef.current.add(d.id);
            tuntutDoc(uid, d.id)
                .then(data => {
                    dicerminkanRef.current.delete(d.data.productId);
                    if (data) pengendaliRef.current.onGagal(d.data.productId, gagal(data));
                    else pengendaliRef.current.onHilang(d.data.productId);
                })
                .catch(e => {
                    dikendaliRef.current.delete(d.id);
                    console.warn('[HasilTertinggal] Tuntut doc gagal:', d.id, e?.message);
                });
        };

        const ambilHasil = async (docId, pid) => {
            dijadualRef.current.delete(docId);
            if (sesiIni.current.aktif.has(pid) || sesiIni.current.milik.has(docId)) return;
            dikendaliRef.current.add(docId);
            // Sediakan kad (muat teks kad split) SEBELUM menuntut doc — gagal di sini tidak
            // menghilangkan hasil: doc kekal, cuba lagi selepas 30s.
            try {
                await pengendaliRef.current.sedia?.(pid);
            } catch (e) {
                console.warn('[HasilTertinggal] Kad belum sedia, cuba lagi 30s:', pid, e?.message);
                dikendaliRef.current.delete(docId);
                dijadualRef.current.set(docId, setTimeout(() => ambilHasil(docId, pid), 30000));
                return;
            }
            if (sesiIni.current.aktif.has(pid)) { dikendaliRef.current.delete(docId); return; }
            tuntutDoc(uid, docId)
                .then(async (data) => {
                    dicerminkanRef.current.delete(pid);
                    if (!data) { pengendaliRef.current.onHilang(pid); return; }
                    const hasil = bacaHasil(data);
                    if (!hasil) { pengendaliRef.current.onGagal(pid, { jenis: 'rosak' }); return; }
                    await pengendaliRef.current.onSiap(pid, hasil);
                })
                .catch(e => {
                    dikendaliRef.current.delete(docId);
                    console.warn('[HasilTertinggal] Ambil hasil gagal:', docId, e?.message);
                });
        };

        for (const d of docs) {
            if (milik.has(d.id) || dikendaliRef.current.has(d.id)) continue;
            const kelas = kelasDocProgress(d.data, sekarang);
            if (kelas === 'abaikan') continue;
            if (kelas === 'buang') {
                dikendaliRef.current.add(d.id);
                padamDocProgress(uid, d.id);
                continue;
            }
            if (!kadSudahDimuat) continue;            // tunggu senarai kad dimuat dahulu
            const pid = d.data.productId;
            if (aktif.has(pid)) continue;             // page ini sedang menjana kad ini
            if (!kadIkutId.has(pid)) {
                // Kad sudah dipadam — doc tiada guna lagi (biar yang masih berjalan habis dahulu).
                if (kelas !== 'jalan') { dikendaliRef.current.add(d.id); padamDocProgress(uid, d.id); }
                continue;
            }

            if (kelas === 'jalan') {
                const kunci = kunciStatus(d.data);
                if (statusRef.current.get(d.id) !== kunci) {
                    statusRef.current.set(d.id, kunci);
                    dicerminkanRef.current.set(pid, d.id);
                    pengendaliRef.current.onJalan(pid, d.data);
                }
            } else if (kelas === 'siap') {
                if (!dijadualRef.current.has(d.id)) {
                    dicerminkanRef.current.set(pid, d.id);
                    const t = setTimeout(() => ambilHasil(d.id, pid), bakiTungguPemilik(d.data, sekarang));
                    dijadualRef.current.set(d.id, t);
                }
            } else if (kelas === 'ralat') {
                tuntutDanGagal(d, data => ({ jenis: 'ralat', mesej: data.ralat || '', kod: data.kod || null }));
            } else if (kelas === 'mati') {
                tuntutDanGagal(d, () => ({ jenis: 'mati' }));
            } else if (kelas === 'siapTanpaHasil') {
                tuntutDanGagal(d, data => ({ jenis: data.hasilTerlaluBesar ? 'terlaluBesar' : 'kosong' }));
            }
        }

        // Doc yang hook sedang papar loading-nya hilang (diambil page lain) → buang loading.
        // Hasil sebenar sampai melalui langganan kad seperti biasa.
        for (const [pid, docId] of dicerminkanRef.current) {
            if (!idAda.has(docId) && !dikendaliRef.current.has(docId) && !dijadualRef.current.has(docId)) {
                dicerminkanRef.current.delete(pid);
                statusRef.current.delete(docId);
                if (!aktif.has(pid)) pengendaliRef.current.onHilang(pid);
            }
        }
    }, [uid, docs, products, sesiIni]);
}
